/**
 * Job search page, can be accessed by going to /jobs.
 */

import { GetServerSideProps, NextPage } from 'next';
import React, { useEffect } from 'react';
import initApollo, { addApolloState } from '~/jobiqo/apollo/initApollo';
import { JobSearchSplit } from '~/jobiqo/components/JobSearch/SplitView';
import {
  getSearchQueryStringObject,
  getSearchRoute,
  checkUnknownRoute,
  removeRouteInfoFromURL,
} from '~/jobiqo/lib/search';
import { getQueryVariables } from '~/jobiqo/components/Search/utils/searchHelpers';
import { FacetConfig } from '~/jobiqo/models/search';
import { removeUnusedItemsFromUrl } from '~/jobiqo/pages/jobs/utils';
import { GdprConsentProvider } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { jobSearchQuery } from '~/jobiqo/pages/jobs/graphql/queries/jobSearch';
import JobAlertProvider from '~/jobiqo/components/JobAlert/context';
import JobSearchProvider from '~/jobiqo/context/jobSearch';
import { getGSSPPageData } from '~/jobiqo/lib/data/getPageData';
import { commonQueries } from '~/jobiqo/lib/data/commonQueries';
import { isAnonymousByHeaders } from '~/jobiqo/lib/auth';
import { getOriginalLink } from '~/jobiqo/utils/links';
import {
  addSearchUrlItem,
  getSearchUrlItems,
  removeSearchUrlItem,
  URLfilter,
} from '~/jobiqo/services/searchUrlItems';
import {
  getAppConfig,
  getEnabledModules,
  getServerConfigByName,
} from '~/jobiqo/lib/config';
import { getJobById } from '~/jobiqo/components/Job/graphql/queries/jobQueries';
import { Job } from '~/jobiqo/types/types';
import { isRedirectCode, isPermanentRedirectCode } from '~/jobiqo/lib/redirect';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

interface JobSearchProps {
  querystring: any;
  loading: boolean;
  error: boolean;
  data: any;
  urlItemsToAdd: URLfilter[];
  initialJob: Job | null;
  enabledModules: string[];
}

const JobSearchPage: NextPage<JobSearchProps> = ({
  querystring,
  loading,
  error,
  data,
  urlItemsToAdd,
  initialJob,
  enabledModules,
}) => {
  useEffect(() => {
    // Start by clearing old items that are not in the current url and re-adding the new ones.
    const currentItems = getSearchUrlItems();
    const itemsToRemove = currentItems.filter(
      (item) => !urlItemsToAdd.find((newItem) => newItem.key === item.key),
    );
    itemsToRemove.forEach((item) => {
      removeSearchUrlItem(item.key);
    });

    // Add the items to the clean url.
    urlItemsToAdd.forEach((item) => {
      addSearchUrlItem(item);
    });
  }, [urlItemsToAdd]);

  return (
    <GdprConsentProvider>
      <JobAlertProvider>
        <JobSearchProvider
          initialQuerystring={querystring}
          initialJob={initialJob}
          enabledModules={enabledModules}
        >
          <JobSearchSplit
            querystring={querystring}
            loading={loading}
            error={error}
            data={data}
          />
        </JobSearchProvider>
      </JobAlertProvider>
    </GdprConsentProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const frontendConfig = getServerFrontendConfig(ctx);
  const JOBS_ITEMS_PER_PAGE = frontendConfig.JOBS.JOBS_LISTING_NUMBER;
  const jobSearchFacetsConfig: FacetConfig[] = Object.values(
    frontendConfig.JOBS.FACETS,
  );
  const CLEAN_FACETS_ORDER = frontendConfig.JOBS.SEO.CLEAN_FACETS_ORDER;
  const pageData = await getGSSPPageData(ctx);
  const backendConfig = await getAppConfig(ctx);
  const enabledModules = getEnabledModules(backendConfig);
  const WATCHADO_ENABLED: boolean = !!getServerConfigByName(
    backendConfig,
    'jobiqo_watchadoo',
  )?.value[0];
  const SITE_CAMPAIGN_ENABLED: boolean = !!getServerConfigByName(
    backendConfig,
    'site_campaign_enabled',
  )?.value[0];

  const { query, locale } = ctx;

  // Returns information about search data,
  // including clean url data.
  const route: any = await getSearchRoute(ctx);

  // Additional check if we get the redirect from the backend.
  // We check 302 for debugging purposes.
  if (isRedirectCode(route?.code) && ctx.resolvedUrl !== route?.target) {
    return {
      redirect: {
        destination: route.target,
        permanent: isPermanentRedirectCode(route?.code),
      },
      props: {},
    };
  }

  // We still needs information about these (clean urls)
  // in the querystring object.
  const { urlItemsToAdd, newQueryString } = getSearchQueryStringObject(
    query,
    route,
    ctx,
  );

  // we need to remove route from the resolvedUrl and this will be "path".
  const cleanedPath = removeUnusedItemsFromUrl(
    removeRouteInfoFromURL(ctx.resolvedUrl, route, { ...query }),
  );

  // Since the page could be translated we need to pass the backend the
  // url the backend knows.
  const pageActualURL = getOriginalLink(cleanedPath, ctx.locale);

  const notFound = checkUnknownRoute(
    route,
    query,
    newQueryString,
    CLEAN_FACETS_ORDER,
  );

  if (notFound) {
    return {
      notFound: true,
    };
  }

  if (route?.statusCode && ctx.res) {
    ctx.res.statusCode = route.statusCode;
  }

  const apolloClient = initApollo({}, ctx);

  await commonQueries(ctx, apolloClient, cleanedPath, {
    siteCampaignEnabled: SITE_CAMPAIGN_ENABLED,
  });

  const {
    loading,
    error,
    previousData,
    data = previousData,
  } = await apolloClient.query({
    query: jobSearchQuery(WATCHADO_ENABLED),
    variables: {
      ...getQueryVariables(
        newQueryString,
        JOBS_ITEMS_PER_PAGE,
        jobSearchFacetsConfig,
        locale,
      ),
      searchPath: pageActualURL,
    },
  });

  // Fetch job data only for an explicit URL selection.
  // The feature flag controls implicit auto-selection of the first job,
  // but direct job URLs should keep working regardless of that flag.
  let initialJob: Job | null = null;
  const jobId = newQueryString?.jobId ? String(newQueryString.jobId) : null;
  const backfillid = newQueryString?.backfillid;

  if (jobId) {
    // Fetch regular and backfill jobs via the same query.
    const jobIdNumber = Number.parseInt(jobId as string, 10);
    const jobResult = await apolloClient.query({
      query: getJobById(),
      variables: {
        id: jobIdNumber,
        backfillid: backfillid || null,
        getOrgProfileDetails: true,
      },
    });
    initialJob = jobResult?.data?.jobById?.job || null;
  }

  if (isAnonymousByHeaders(ctx.req)) {
    ctx.res.setHeader(
      'Cache-Control',
      'public, s-maxage=1800, stale-while-revalidate',
    );
  }

  return addApolloState(apolloClient, {
    props: {
      ...pageData,
      path: cleanedPath,
      querystring: newQueryString,
      loading,
      data,
      error: error ? true : false,
      urlItemsToAdd,
      initialJob,
      enabledModules,
    },
  });
};

export default JobSearchPage;
